import React, { Fragment, useEffect, useState } from 'react'
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  ClickAwayListener,
  FormControl,
  Grow,
  Grid,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { WarehouseDDL } from '../../../components/Almacen'
import {
  useOrderOrigins,
  useProductOrigins,
  useTypeAsnIncome,
  useTypePosition,
  useTypeMovement,
  useWarehouses,
  useWorkOrderState,
  useZones,
  useAsnState
} from '../../../hooks'
import { useCourier } from '../../../hooks/useCourier'
import { useSuppliers } from '../../../hooks/useSuppliers'
import { Courier } from '../../../interfaces/Courier'
import { Filter, FilterComponentProps, FilterOption } from './types'
import { DateTime } from 'luxon'
import { TypePositionDDL } from '../../TypePosition'
import { TypeMovementDDL } from '../../TypeMovement'
import { ProductOrigin } from '../../../interfaces/Producto/ProductOrigins'
import { AsnState } from '../../../interfaces/Asn/AsnState'
import { ZoneDDL } from '../../Zone'
import { useTypesOrder } from '../../../hooks/useTypeOrder'
import { eStatus, eTypeActionExternal } from '../../../enums/eExternalAudit'
//import RotateLeftIcon from '@mui/icons-material/RotateLeft'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import SearchIcon from '@mui/icons-material/Search'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import CleaningServicesIcon from '@mui/icons-material/CleaningServices'
import QueueIcon from '@mui/icons-material/Queue'
import AddIcon from '@mui/icons-material/Add'
import {
  eInventoryCheckState,
  eTypeInventoryCheck
} from '../../../enums/eInventoryCheck'
import { GenericDLL } from '../../Almacen/UI/GenericDLL'
import { eStatusProcess } from '../../../enums/eStatusProcess'
import { useTypeProcess } from '../../../hooks/useTypeProcess'
import {
  eBatchControl,
  eBatchExpiredStatus,
  eIsGreenSale
} from '../../../enums/eBatch'
import { eWebhookEvents } from '../../../enums/eWebhookEvents'
import { useFlows } from '../../../hooks/useFlows'
import { useTypesAlert } from '../../../hooks/useTypeAlert'
import { sortWithCustomOrder } from '../SortOrderList/GeneralOrder'
import { DEFAULT_WORKORDER_STATE_ORDER } from './config'
import { useTypeVas } from '../../../hooks/useTypeVas'
import { TypeVasDDL } from '../../TypeVas/TypeVasDDL'
import { useTypeStorage } from '../../../hooks/useTypeStorage'
import { TypeStorageDLL } from '../../TypeStorage/TypeStorageDLL'
import {TypePositionAutocomplete} from '../../../pages/NewTypePosition/components/TypePositionAutocomplete'
import { PositionAutoComplete } from '../../PositionNew/components/PositionAutoComplete'

export const FilterComponent: React.FC<FilterComponentProps> = ({
  options,
  setSelectedFilters,
  selectedFilters,
  onSearchClick,
  includeAddButton = false,
  requiredFilterKeys,
  defaultSelectedFilterKey,
  includeSearchOnClean,
  onAddButtonClick,
  addButtonTitle,
  createButtonTooltip,
  extraActions,
  rightSlot
}) => {
  const {
    couriersState: { couriers }
  } = useCourier()

  const {
    suppliersState: { suppliers },
    getSuppliers
  } = useSuppliers()

  const {
    warehousesState: { warehouses }
  } = useWarehouses()

  const {
    getTypeAsnIncome,
    typeAsnIncomeState: { typeAsnIncome }
  } = useTypeAsnIncome()

  const {
    workOrderStatesState: { workOrderStates }
  } = useWorkOrderState()

  const {
    typePositionsState: { typePositions }
  } = useTypePosition()

  const {
    typeMovementsState: { typeMovements },
    getTypeMovements
  } = useTypeMovement()

  const {
    getProductOrigins,
    productOriginsState: { productOrigins }
  } = useProductOrigins()

  const {
    zonesState: { zones }
  } = useZones()

  const {
    typesOrderState: { typesOrder }
  } = useTypesOrder()

  const {
    getOrderOrigins,
    orderOriginsState: { orderOrigins }
  } = useOrderOrigins()

  const {
    getTypeProcess,
    typeProcessState: { typeProcess }
  } = useTypeProcess()

  const {
    getTypesAlert,
    typesAlertState: { typesAlert }
  } = useTypesAlert()

  const {
    getFlows,
    flowsState: { flows }
  } = useFlows()

  const {
    asnStatesState: { asnState: asnStates },
    getAsnState
  } = useAsnState()

  const {
    typeVasState: { typeVas },
    getTypeVas
  } = useTypeVas()

  const {
    typeStorageState: { typestorage },
    getTypeStorage
  } = useTypeStorage()

  const [dateRangeError, setDateRangeError] = useState<string>('')
  const [selectedFilterKey, setSelectedFilterKey] = useState<string | null>(
    defaultSelectedFilterKey || null
  )

  const [inputValue, setInputValue] = useState<string>('') // Estado para manejar el valor de entrada
  const [availableOptions, setAvailableOptions] = useState<FilterOption[]>([])
  const [searchMenuOpen, setSearchMenuOpen] = useState(false)
  const searchButtonGroupRef = React.useRef<HTMLDivElement>(null)
  const [extraMenuOpen, setExtraMenuOpen] = useState(false)
  const extraMenuRef = React.useRef<HTMLButtonElement>(null)
  const handleExtraMenuToggle = () => setExtraMenuOpen((prev) => !prev)
  const handleExtraMenuClose = () => setExtraMenuOpen(false)

  // Resuelve las keys/labels de Filter que genera un FilterOption de tipo
  // 'daterange' según su key (única fuente de verdad, reutilizada también
  // por addFilter() para no duplicar este mapeo)
  const getDateRangeMeta = (option: FilterOption) => {
    if (option.key === 'expirationDateRange') {
      return {
        initialKey: 'initialExpirationDateFilter',
        finalKey: 'finalExpirationDateFilter',
        initialLabel: 'Fecha Inicial Expiración',
        finalLabel: 'Fecha Final Expiración'
      }
    }
    if (option.key === 'movementDateRange') {
      return {
        initialKey: 'initialDateFilter',
        finalKey: 'finalDateFilter',
        initialLabel: 'Fecha Inicial Movimiento',
        finalLabel: 'Fecha Final Movimiento'
      }
    }
    return {
      initialKey: 'initialDateFilter',
      finalKey: 'finalDateFilter',
      initialLabel: 'Fecha Inicial',
      finalLabel: 'Fecha Final'
    }
  }

  // Resuelve TODAS las keys de Filter que puede generar una FilterOption al
  // agregarse. Para 'range'/'daterange' el key de la opción no coincide con
  // las keys de los Filter generados (ej. 'maxCapacityRange' -> 'initialMaxCapacity'
  // + 'finalMaxCapacity'), así que no alcanza con comparar option.key contra
  // selectedFilters directamente.
  const getGeneratedFilterKeys = (option: FilterOption): string[] => {
    if (option.type === 'range') {
      return [
        option.rangeConfig?.initialKey || `${option.key}From`,
        option.rangeConfig?.finalKey || `${option.key}To`
      ]
    }
    if (option.type === 'daterange') {
      const { initialKey, finalKey } = getDateRangeMeta(option)
      return [initialKey, finalKey]
    }
    return [option.key]
  }

  useEffect(() => {
    setAvailableOptions(
      options.filter(
        (el) =>
          // Si el filtro puede ser repetido, SIEMPRE debe estar disponible
          el.canBeRepeated ||
          // Si no puede ser repetido, solo filtrarlo si alguna de sus keys
          // generadas ya existe en selectedFilters (ej: 'range'/'daterange'
          // generan 2 keys distintas al key de la propia opción)
          !getGeneratedFilterKeys(el).some((key) =>
            selectedFilters.some((filter) => filter.key === key)
          )
      )
    )
  }, [selectedFilters, options])

  // guardar rango fechas filtros
  const [dateRangeStart, setDateRangeStart] = useState<string>('')
  const [dateRangeEnd, setDateRangeEnd] = useState<string>('')

  const handleDateRangeStartChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newStartDate = event.target.value
    setDateRangeStart(newStartDate)
    validateDateRange(newStartDate, dateRangeEnd)
  }

  const handleDateRangeEndChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newEndDate = event.target.value
    setDateRangeEnd(newEndDate)
    validateDateRange(dateRangeStart, newEndDate)
  }

  // validar rango fechas
  const validateDateRange = (startDate: string, endDate: string) => {
    if (startDate && endDate) {
      const start = new Date(startDate)
      const end = new Date(endDate)

      if (start > end) {
        setDateRangeError('Fecha inicial mayor a la final')
      } else {
        setDateRangeError('')
      }
    } else {
      setDateRangeError('')
    }
  }

  // guardar rango numérico genérico (ej: capacidad máxima desde/hasta)
  const [rangeStart, setRangeStart] = useState<string>('')
  const [rangeEnd, setRangeEnd] = useState<string>('')
  const [rangeError, setRangeError] = useState<string>('')

  // validar rango numérico: permite rango abierto (solo desde o solo hasta)
  const validateNumericRange = (start: string, end: string) => {
    if (start !== '' && end !== '' && Number(start) > Number(end)) {
      setRangeError('El valor "desde" no puede ser mayor que "hasta"')
    } else {
      setRangeError('')
    }
  }

  const handleRangeStartChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newStart = event.target.value
    setRangeStart(newStart)
    validateNumericRange(newStart, rangeEnd)
  }

  const handleRangeEndChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newEnd = event.target.value
    setRangeEnd(newEnd)
    validateNumericRange(rangeStart, newEnd)
  }

  useEffect(() => {
    if (orderOrigins?.length === 0) {
      getOrderOrigins()
    }

    if (productOrigins?.length === 0) {
      getProductOrigins()
    }

    if (typeProcess?.length === 0) {
      getTypeProcess()
    }
    if (flows?.length === 0) {
      getFlows()
    }
    if (typesAlert?.length === 0) {
      getTypesAlert()
    }
    if (typeMovements?.length === 0) {
      getTypeMovements()
    }

    if (asnStates?.length === 0) {
      getAsnState()
    }

    if (typeAsnIncome?.length === 0) {
      getTypeAsnIncome()
    }

    if (typeVas?.length === 0) {
      getTypeVas()
    }

    if (typestorage?.length === 0) {
      getTypeStorage()
    }

    if (suppliers?.length === 0) {
      getSuppliers()
    }
  }, [])

  // Determinar la opción seleccionada en base a selectedFilterKey
  const selectedOption = options.find(
    (option) => option.key === selectedFilterKey
  )

  const handleFilterSelection = (event: SelectChangeEvent<string>) => {
    setSelectedFilterKey(event.target.value as string)
    // Limpiar el inputValue cuando se cambia de filtro para evitar valores residuales
    setInputValue('')
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value.toString())
  }

  const handleInputChangeNumericNotNegative = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value

    // Validar que solo se ingresen números no negativos
    if (/^\d*$/.test(value) || value === '') {
      setInputValue(value === '' ? '' : parseInt(value, 10).toString())
    }
  }

  const handleSearchButton = () => {
    if (selectedOption?.type === 'text' && includeAddButton) {
      addFilter()
      onSearchClick(selectedFilters)
      return
    }

    let filtersToSearch = selectedFilters
    if (
      selectedOption?.type === 'textempty' ||
      (selectedFilterKey && selectedOption && !includeAddButton)
    ) {
      if (inputValue !== '-1') {
        // Replicar la lógica de addFilter para obtener el nuevo array sin depender del estado actualizado
        let displayValue = getDisplayValue(selectedOption.key)
        let filterValue = inputValue
        if (
          selectedOption.type === 'text' ||
          selectedOption.type === 'textempty'
        ) {
          filterValue = inputValue.trim()
        } else if (
          selectedOption.type === 'custom' &&
          selectedOption.transformForBackend
        ) {
          filterValue = selectedOption.transformForBackend(inputValue)
          if (!displayValue && selectedOption.transformForDisplay) {
            displayValue = selectedOption.transformForDisplay(inputValue)
          }
        } else if (selectedOption.type === 'custom') {
          try {
            const parsed = JSON.parse(inputValue)
            filterValue = parsed.id ? parsed.id.toString() : inputValue
            displayValue = parsed.displayValue || inputValue
          } catch {
            filterValue = inputValue
            displayValue = inputValue
          }
        } else if (selectedOption.type === 'yesornotoption') {
          displayValue = inputValue === 'true' ? 'Sí' : 'No'
        }
        const exists = selectedFilters.filter(
          ({ key, value }) =>
            selectedOption.key === key && value === filterValue
        )
        if (exists.length === 0) {
          const newFilter: Filter = {
            key: selectedOption.key,
            label: selectedOption.label,
            value: filterValue,
            displayValue: displayValue || inputValue
          }
          filtersToSearch = [...selectedFilters, newFilter]
          addFilter()
        }
      }
    }
    onSearchClick(filtersToSearch)
  }

  const getDisplayValue = (option: string) => {
    switch (option) {
      case 'warehouseId':
        return (
          warehouses?.find((el) => el.id === parseInt(inputValue))
            ?.warehouseName || ''
        )
      case 'workOrderStateId':
        return (
          workOrderStates?.find(
            (el) => el.workOrderStateId === parseInt(inputValue)
          )?.state || ''
        )

      case 'courierId':
        return (
          couriers?.find((el) => el.courierId === parseInt(inputValue))?.name ||
          ''
        )

      case 'courierServiceId': {
        if (!couriers) return ''

        let serviceName = ''
        let courierName = ''

        // 1. Buscamos el courier padre que contenga este servicio
        const parentCourier = couriers.find((courier) =>
          courier.courierServices.some(
            (service) => service.courierServiceId === parseInt(inputValue)
          )
        )

        // 2. Si lo encontramos, obtenemos los nombres
        if (parentCourier) {
          courierName = parentCourier.name
          const service = parentCourier.courierServices.find(
            (s) => s.courierServiceId === parseInt(inputValue)
          )
          serviceName = service ? service.name : ''
        }

        // 3. Retornamos el mismo formato que el dropdown
        if (courierName && serviceName) {
          return `${courierName} - ${serviceName}`
        }

        return serviceName || '' // Fallback por si algo falla
      }

      case 'typePositionId':
        return (
          typePositions?.find(
            (el) => el.typePositionId === parseInt(inputValue)
          )?.typePositionDescription || ''
        )
      case 'typeMovementId':
        return (
          typeMovements?.find(
            (el) => el.typeMovementId === parseInt(inputValue)
          )?.description || ''
        )
      case 'externalStatusId':
        return eStatus[parseInt(inputValue)] || ''
      case 'typeActionId':
        return eTypeActionExternal[parseInt(inputValue)] || ''
      case 'productOriginId':
      case 'brandId': {
        return (
          productOrigins?.find(
            (el) => el.productOriginId === parseInt(inputValue)
          )?.name || ''
        )
      }

      case 'zoneId':
        return (
          zones?.find((zone) => zone.zoneId === parseInt(inputValue))
            ?.zoneName || ''
        )
      case 'typeOrderId':
        return (
          typesOrder?.find((el) => el.typeOrderId === parseInt(inputValue))
            ?.name || ''
        )
      case 'orderStateId': {
        const orderStates = [
          { label: 'Creado', value: 1 },
          { label: 'En Espera De Stock', value: 4 },
          { label: 'En Preparación', value: 5 },
          { label: 'Solicitud De Cancelación', value: 2 },
          { label: 'Cancelado', value: 3 },
          { label: 'Gestión Completada', value: 6 }
        ]
        return (
          orderStates.find((state) => state.value === parseInt(inputValue))
            ?.label || ''
        )
      }
      case 'inventoryCheckStateId':
        return eInventoryCheckState[parseInt(inputValue)] || ''
      case 'typeInventoryCheckId':
        return eTypeInventoryCheck[parseInt(inputValue)] || ''
      case 'orderOriginId':
        return (
          orderOrigins?.find((el) => el.orderOriginId === parseInt(inputValue))
            ?.origin || ''
        )
      case 'typeProcessId':
        return (
          typeProcess?.find((el) => el.typeProcessId === parseInt(inputValue))
            ?.description || ''
        )
      case 'supplierId':
        return (
          suppliers?.find((el) => el.supplierId === parseInt(inputValue))
            ?.supplierName || ''
        )
      case 'flowId':
        return (
          flows?.find((el) => el.flowId === parseInt(inputValue))
            ?.description || ''
        )
      case 'typeAlertId':
        return (
          typesAlert?.find((el) => el.typeAlertId === parseInt(inputValue))
            ?.name || ''
        )
      case 'typeAsnIncomeId':
        return (
          typeAsnIncome?.find(
            (el) => el.typeAsnIncomeId === parseInt(inputValue)
          )?.name || ''
        )
      case 'StatusProcessId':
        return eStatusProcess[parseInt(inputValue)] || ''
      case 'batchExpiredStatus':
        return eBatchExpiredStatus[parseInt(inputValue)] || ''
      case 'batchControl':
        return eBatchControl[parseInt(inputValue)] || ''
      case 'active':
      case 'externalStorage':
      case 'withVas':
        return inputValue === 'true' ? 'Sí' : inputValue === 'false' ? 'No' : ''
      case 'isDraft':
        return inputValue === 'true' ? 'Sí' : inputValue === 'false' ? 'No' : ''
      case 'asnStateId':
      case 'detailAsnStateId':
        return (
          asnStates?.find(
            (el: AsnState) => el.asnStateId === parseInt(inputValue)
          )?.name || inputValue
        )
      case 'isGreenSale':
        return eIsGreenSale[parseInt(inputValue)] || ''
      case 'typeVasId':
        return (
          typeVas?.find((el) => el.typeVasId === parseInt(inputValue))?.name ||
          ''
        )
      case 'typeStorageId':
        return (
          typestorage?.find(
            (el) => el.typeStorageId === parseInt(inputValue)
          )?.name || ''
        )
      case 'initialDateFilter':
      case 'finalDateFilter':
      case 'initialExpirationDateFilter':
      case 'finalExpirationDateFilter':
        return inputValue // Las fechas ya vienen en formato correcto
      case 'webhookEventsIds':
        return eWebhookEvents[parseInt(inputValue)] || ''
      default:
        return undefined
    }
  }

  const isAddFilterButtonDisabled = (): boolean => {
    if (!selectedFilterKey) {
      return true
    }

    // Para daterange: verificar que ambas fechas estén seleccionadas y no haya errores
    if (selectedOption?.type === 'daterange') {
      return !dateRangeStart || !dateRangeEnd || !!dateRangeError
    }

    // Para range: permite rango abierto, basta con que uno de los dos tenga valor
    // y que no haya error de validación (desde > hasta)
    if (selectedOption?.type === 'range') {
      return (!rangeStart && !rangeEnd) || !!rangeError
    }

    // Para textempty: siempre habilitado si hay filtro seleccionado
    if (selectedOption?.type === 'textempty') {
      return false
    }

    // Verificar si el valor es válido (no es -1)
    const isValidValue = () => {
      if (!inputValue || inputValue.length === 0) {
        return false
      }

      // Verificar si el valor es -1 (opción por defecto inválida)
      if (inputValue === '-1') {
        return false
      }

      // Para campos numéricos, verificar también como número
      if (
        selectedOption?.type === 'numeric' ||
        selectedOption?.type === 'numericnotnegative'
      ) {
        const numValue = parseInt(inputValue, 10)
        if (numValue === -1) {
          return false
        }
      }

      return true
    }

    // Para otros tipos: verificar que haya un valor válido (no -1)
    return !isValidValue()
  }

  const addFilter = () => {
    if (selectedOption?.type === 'daterange') {
      // Validar que ambas fechas estén presentes
      if (!dateRangeStart || !dateRangeEnd) {
        return
      }
      // Validar que no haya error en el rango de fechas
      if (dateRangeError) {
        return
      }
      // Determinar los keys según el tipo de rango
      const { initialKey, finalKey, initialLabel, finalLabel } =
        getDateRangeMeta(selectedOption)
      // Crear filtro para fecha inicial
      const initialDateFilter: Filter = {
        key: initialKey,
        label: initialLabel,
        value: dateRangeStart,
        displayValue: dateRangeStart
      }
      // Crear filtro para fecha final
      const finalDateFilter: Filter = {
        key: finalKey,
        label: finalLabel,
        value: dateRangeEnd,
        displayValue: dateRangeEnd
      }
      // Verificar que no existan ya estos filtros
      const existsInitial = selectedFilters.some(
        ({ key }) => key === initialKey
      )
      const existsFinal = selectedFilters.some(({ key }) => key === finalKey)
      if (existsInitial || existsFinal) {
        return
      }
      // Agregar los dos filtros
      setSelectedFilters([
        ...selectedFilters,
        initialDateFilter,
        finalDateFilter
      ])
      // resetea los valores en el estado que tenian por defecto
      setDateRangeStart('')
      setDateRangeEnd('')
      setInputValue('')
      // Eliminar la opción de availableOptions solo si NO es repetible
      if (!selectedOption.canBeRepeated) {
        setAvailableOptions(
          availableOptions.filter((option) => option.key !== selectedOption.key)
        )
      }
      setSelectedFilterKey('')
      setDateRangeError('')
      return
    }
    if (selectedOption?.type === 'range') {
      // Rango abierto: requiere al menos uno de los dos valores
      if (!rangeStart && !rangeEnd) {
        return
      }
      // Validar que no haya error en el rango (desde > hasta)
      if (rangeError) {
        return
      }
      // Resolver las keys/labels desde rangeConfig, con fallback genérico
      const initialKey =
        selectedOption.rangeConfig?.initialKey || `${selectedOption.key}From`
      const finalKey =
        selectedOption.rangeConfig?.finalKey || `${selectedOption.key}To`
      const initialLabel =
        selectedOption.rangeConfig?.initialLabel ||
        `${selectedOption.label} Desde`
      const finalLabel =
        selectedOption.rangeConfig?.finalLabel ||
        `${selectedOption.label} Hasta`
      // Verificar que no existan ya estos filtros
      const existsInitial = selectedFilters.some(({ key }) => key === initialKey)
      const existsFinal = selectedFilters.some(({ key }) => key === finalKey)
      if (existsInitial || existsFinal) {
        return
      }
      const newFilters: Filter[] = []
      if (rangeStart) {
        newFilters.push({
          key: initialKey,
          label: initialLabel,
          value: rangeStart,
          displayValue: rangeStart
        })
      }
      if (rangeEnd) {
        newFilters.push({
          key: finalKey,
          label: finalLabel,
          value: rangeEnd,
          displayValue: rangeEnd
        })
      }
      setSelectedFilters([...selectedFilters, ...newFilters])
      // resetea los valores en el estado que tenian por defecto
      setRangeStart('')
      setRangeEnd('')
      setInputValue('')
      // Eliminar la opción de availableOptions solo si NO es repetible
      if (!selectedOption.canBeRepeated) {
        setAvailableOptions(
          availableOptions.filter((option) => option.key !== selectedOption.key)
        )
      }
      setSelectedFilterKey('')
      setRangeError('')
      return
    }
    if (
      selectedOption?.type === 'textempty' ||
      (selectedOption && inputValue)
    ) {
      let displayValue = getDisplayValue(selectedOption.key)
      // Para componentes custom con transformadores, usar el transformador correspondiente
      let filterValue = inputValue
      if (
        selectedOption.type === 'text' ||
        selectedOption.type === 'textempty'
      ) {
        filterValue = inputValue.trim()
      } else if (
        selectedOption.type === 'custom' &&
        selectedOption.transformForBackend
      ) {
        filterValue = selectedOption.transformForBackend(inputValue)
        // Si no se pudo obtener displayValue de getDisplayValue, usar el transformador de display
        if (!displayValue && selectedOption.transformForDisplay) {
          displayValue = selectedOption.transformForDisplay(inputValue)
        }
      } else if (selectedOption.type === 'custom') {
        // Fallback: intentar extraer el id y displayValue del JSON
        try {
          const parsed = JSON.parse(inputValue)
          filterValue = parsed.id ? parsed.id.toString() : inputValue
          displayValue = parsed.displayValue || inputValue
        } catch {
          // Si no se puede parsear, usar el valor original
          filterValue = inputValue
          displayValue = inputValue
        }
      } else if (selectedOption.type === 'yesornotoption') {
        displayValue = inputValue === 'true' ? 'Sí' : 'No'
      }
      const exists = selectedFilters.filter(
        ({ key, value }) => selectedOption.key === key && value === filterValue
      )
      if (exists.length > 0) {
        return
      }
      const newFilter: Filter = {
        key: selectedOption.key,
        label: selectedOption.label,
        value: filterValue,
        displayValue: displayValue || inputValue
      }
      setSelectedFilters([...selectedFilters, newFilter])
      setInputValue('') // Limpiar el valor de entrada después de agregar el filtro
      // Eliminar la opción seleccionada de availableOptions solo si NO es repetible
      if (!selectedOption.canBeRepeated) {
        setAvailableOptions(
          availableOptions.filter((option) => option.key !== selectedOption.key)
        )
      }
      setSelectedFilterKey('')
    }
  }

  const handleDeleteFilter = (filterToDelete: Filter) => {
    // obtener opción
    const deletedOption = options.find(
      (option) => option.key === filterToDelete.key
    )
    // detectar si es repetible
    if (deletedOption?.canBeRepeated) {
      // Para repetibles: solo eliminar el filtro específico (por key y value)
      const updatedFilters = selectedFilters.filter(
        (filter) =>
          !(
            filter.key === filterToDelete.key &&
            filter.value === filterToDelete.value
          )
      )
      setSelectedFilters(updatedFilters)
      // Si includeSearchOnClean está activo, dispara la búsqueda con los filtros actualizados
      if (includeSearchOnClean && onSearchClick) {
        onSearchClick(updatedFilters)
      }
      // NO agregar de vuelta a availableOptions porque los repetibles siempre deben estar ahí
    } else {
      // Para no repetibles: eliminar todos los filtros con ese key
      const updatedFilters = selectedFilters.filter(
        (filter) => filter.key !== filterToDelete.key
      )
      setSelectedFilters(updatedFilters)
      // Si includeSearchOnClean está activo, dispara la búsqueda con los filtros actualizados
      if (includeSearchOnClean && onSearchClick) {
        onSearchClick(updatedFilters)
      }
      // Buscar si la key borrada corresponde a un filtro 'range' genérico
      // (desde/hasta), para devolver esa opción al selector si no queda ninguna
      // de sus dos keys activa
      const rangeOption = options.find(
        (option) =>
          option.type === 'range' &&
          (option.rangeConfig?.initialKey === filterToDelete.key ||
            option.rangeConfig?.finalKey === filterToDelete.key)
      )
      // Verifica si se eliminaron ambos filtros de fecha para devolver la opcion dateRange
      if (
        filterToDelete.key === 'initialDateFilter' ||
        filterToDelete.key === 'finalDateFilter'
      ) {
        // Verificar si después de eliminar este filtro, ya no quedan filtros de fecha
        const remainingDateFilters = updatedFilters.filter(
          (filter) =>
            filter.key === 'initialDateFilter' ||
            filter.key === 'finalDateFilter'
        )
        // Solo devolver la opcion dateRange si NO quedan filtros de fecha
        if (remainingDateFilters.length === 0) {
          const dateRangeOption = options.find(
            (option) => option.key === 'dateRange'
          )
          if (
            dateRangeOption &&
            !availableOptions.some((opt) => opt.key === 'dateRange')
          ) {
            setAvailableOptions([...availableOptions, dateRangeOption])
          }
        }
      } else if (rangeOption) {
        // Verificar si después de eliminar este filtro, ya no queda ninguna
        // de las dos keys del rango (desde/hasta)
        const remainingRangeFilters = updatedFilters.filter(
          (filter) =>
            filter.key === rangeOption.rangeConfig?.initialKey ||
            filter.key === rangeOption.rangeConfig?.finalKey
        )
        // Solo devolver la opción de rango si NO quedan filtros de ese rango
        if (
          remainingRangeFilters.length === 0 &&
          !availableOptions.some((opt) => opt.key === rangeOption.key)
        ) {
          setAvailableOptions([...availableOptions, rangeOption])
        }
      } else {
        // Para filtros normales, agregar la opción eliminada nuevamente a availableOptions
        if (
          deletedOption &&
          !availableOptions.some((opt) => opt.key === deletedOption.key)
        ) {
          setAvailableOptions([...availableOptions, deletedOption])
        }
      }
    }
  }

  const handleEditFilter = (filterToEdit: Filter) => {
    // Hay filtros que se manejan por fuera de este componente
    // Si no existe e intentan editarlo, lo borramos
    if (!options.map((option) => option.key).includes(filterToEdit.key)) {
      setSelectedFilters((prev) =>
        prev.filter((el) => el.key !== filterToEdit.key)
      )
      return
    }
    // Agregar la opción eliminada nuevamente a availableOptions solo si NO es repetible
    const deletedOption = options.find(
      (option) => option.key === filterToEdit.key
    )

    // seleccionar esa opción en el filtro
    setSelectedFilterKey(filterToEdit.key)
    setInputValue(filterToEdit.value)

    // detectar si es repetible
    if (deletedOption?.canBeRepeated) {
      // Para repetibles: solo eliminar el filtro específico (por key y value)
      setSelectedFilters(
        selectedFilters.filter(
          (filter) =>
            !(
              filter.key === filterToEdit.key &&
              filter.value === filterToEdit.value
            )
        )
      )
      // NO agregar a availableOptions porque los repetibles siempre deben estar ahí
    } else {
      // Para no repetibles: eliminar el filtro y agregarlo a availableOptions
      setSelectedFilters(
        selectedFilters.filter((filter) => filter.key !== filterToEdit.key)
      )
      // Agregar la opción eliminada nuevamente a availableOptions
      if (
        deletedOption &&
        !availableOptions.some((opt) => opt.key === deletedOption.key)
      ) {
        setAvailableOptions([...availableOptions, deletedOption])
      }
    }
  }

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      handleSearchButton()
    }
  }

  const handleSearchButtonClick = () => {
    handleSearchButton()
  }

  const handleSearchMenuToggle = () => {
    setSearchMenuOpen((prevOpen) => !prevOpen)
  }

  const handleSearchMenuClose = (event?: Event) => {
    if (
      event &&
      searchButtonGroupRef.current &&
      searchButtonGroupRef.current.contains(event.target as HTMLElement)
    ) {
      return
    }
    setSearchMenuOpen(false)
  }

  const handleCleanClick = () => {
    setSelectedFilters([])
    setAvailableOptions(options)
    setSelectedFilterKey('')
    setInputValue('')
    setDateRangeStart('')
    setDateRangeEnd('')
    setDateRangeError('')
    setRangeStart('')
    setRangeEnd('')
    setRangeError('')

    if (includeSearchOnClean) onSearchClick([])
    setSearchMenuOpen(false)
  }

  const isButtonDisabled = (): boolean => {
    // Si no hay botón "Añadir filtro", el botón buscar siempre está habilitado
    if (!includeAddButton) {
      return false
    }

    // Si muestran el botón "agregar filtros"
    // El botón buscar está habilitado cuando hay filtros agregados
    if (includeAddButton) {
      // Deshabilitado si no hay filtros agregados
      if (selectedFilters.length === 0) {
        return true
      }
      // Deshabilitado si hay un filtro siendo editado sin guardar
      return !isAddFilterButtonDisabled()
    }

    // Validación para inputValue = '-1' cuando no hay botón agregar
    if (inputValue === '-1' && !includeAddButton) {
      return true
    }

    // Si se especifican filtros requeridos, validar que al menos uno esté presente
    if (requiredFilterKeys && requiredFilterKeys.length > 0) {
      const hasRequiredFilter = selectedFilters.some((filter) =>
        requiredFilterKeys.includes(filter.key)
      )

      if (!hasRequiredFilter) {
        return true
      }
    }

    // Comportamiento por defecto (sin botón agregar)
    // Validación específica para daterange
    if (selectedOption?.type === 'daterange') {
      return !dateRangeStart || !dateRangeEnd || !!dateRangeError
    }

    // Validación específica para range (rango abierto: basta con un valor)
    if (selectedOption?.type === 'range') {
      return (!rangeStart && !rangeEnd) || !!rangeError
    }

    return selectedOption?.type === 'textempty'
      ? false
      : !selectedOption || inputValue.length === 0
  }
  const setFormatTextChip = (filter: Filter) => {
    switch (filter.key) {
      case 'initialDeliveryDate':
      case 'finalDeliveryDate':
      case 'initialDateFilter':
      case 'finalDateFilter':
      case 'initialExpirationDateFilter':
      case 'finalExpirationDateFilter':
        return `${filter.label}: ${DateTime.fromISO(
          filter.displayValue
        ).toFormat('dd-MM-yyyy')}`
      default:
        return `${filter.label}: ${filter.displayValue}`
    }
  }

  // Se agrega metodo para obtener id de warehouse en caso que esté agregado en los filtros. si sacan los filtros queda null y muestra todas las zonas el DDL de Zona
  const getWarehouseId = () => {
    const id = selectedFilters.find((el) => el.key === 'warehouseId')?.value
    if (id && id > 0) return parseInt(id, 10)

    return null
  }

  const getProductOriginId = () => {
    const id = selectedFilters.find((el) => el.key === 'productOriginId')?.value
    if (id && id > 0) return parseInt(id, 10)

    return null
  }

  const renderFilterInput = (
    option: FilterOption,
    inputValue: string,
    handleInputChange: any,
    selectedFilters: Filter[]
  ) => {
    switch (option.type) {
      case 'numeric':
        return (
          <TextField
            fullWidth
            type='number'
            value={inputValue}
            onChange={handleInputChange}
            variant='outlined'
            size='small'
            sx={{ height: '44px', '& .MuiInputBase-root': { height: '44px' } }}
          />
        )
      case 'numericnotnegative':
        return (
          <TextField
            fullWidth
            type='number'
            value={inputValue}
            onChange={handleInputChangeNumericNotNegative}
            variant='outlined'
            size='small'
            inputProps={{ min: 0 }}
            sx={{ height: '44px', '& .MuiInputBase-root': { height: '44px' } }}
          />
        )
      case 'text':
        return (
          <TextField
            fullWidth
            value={inputValue}
            onChange={handleInputChange}
            variant='outlined'
            size='small'
            autoFocus={true}
            onKeyPress={handleKeyPress}
            inputProps={{ maxLength: option.maxLength }}
            sx={{ height: '44px', '& .MuiInputBase-root': { height: '44px' } }}
          />
        )
      case 'textempty':
        return (
          <TextField
            fullWidth
            value={inputValue}
            onChange={handleInputChange}
            variant='outlined'
            size='small'
            inputProps={{ maxLength: option.maxLength }}
            sx={{ height: '44px', '& .MuiInputBase-root': { height: '44px' } }}
          />
        )
      case 'date':
        return (
          <TextField
            type='date'
            value={inputValue}
            onChange={handleInputChange}
            InputLabelProps={{ shrink: true }}
            fullWidth
            variant='outlined'
            size='small'
            sx={{ height: '44px', '& .MuiInputBase-root': { height: '44px' } }}
          />
        )
      case 'range':
        return (
          // Ver comentario en 'daterange': el error va absoluto para no
          // desplazar los inputs dentro del contenedor de 44px
          <Grid container spacing={1} sx={{ position: 'relative' }}>
            <Grid item xs={6}>
              <TextField
                type={option.rangeConfig?.inputType || 'number'}
                label='Desde'
                value={rangeStart}
                onChange={handleRangeStartChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
                variant='outlined'
                size='small'
                error={!!rangeError}
                sx={{
                  height: '44px',
                  '& .MuiInputBase-root': { height: '44px' }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                type={option.rangeConfig?.inputType || 'number'}
                label='Hasta'
                value={rangeEnd}
                onChange={handleRangeEndChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
                variant='outlined'
                size='small'
                error={!!rangeError}
                sx={{
                  height: '44px',
                  '& .MuiInputBase-root': { height: '44px' }
                }}
              />
            </Grid>
            {rangeError && (
              <Tooltip title={rangeError} placement='bottom-start'>
                <Typography
                  variant='caption'
                  color='error'
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: '8px',
                    right: 0,
                    mt: '2px',
                    fontSize: '0.65rem',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {rangeError}
                </Typography>
              </Tooltip>
            )}
          </Grid>
        )
      case 'precreated':
        if (option.key === 'positionCode') {
          return (
            <PositionAutoComplete
              handleChange={handleInputChange}
              positionValue={inputValue}
              fullWidth
              variant='outlined'
              size='small'
            />
          )
        }
        if (option.key === 'externalStatusId') {
          const externalStatusOptions = Object.keys(eStatus)
            .filter((key) => isNaN(Number(key))) // Filtra las claves numéricas
            .map((key) => ({
              value: eStatus[key as keyof typeof eStatus],
              label: key
            }))

          return (
            <TextField
              variant={'outlined'}
              id='statusId'
              name='statusId'
              select
              value={inputValue}
              onChange={handleInputChange}
              fullWidth
              size='small'
              sx={{
                height: '44px',
                '& .MuiInputBase-root': { height: '44px' }
              }}
            >
              <MenuItem key={-1} value={-1}>
                {'Seleccionar Estado...'}
              </MenuItem>
              {externalStatusOptions.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </TextField>
          )
        }
        if (option.key === 'typeActionId') {
          const externalStatusOptions = Object.keys(eTypeActionExternal)
            .filter((key) => isNaN(Number(key))) // Filtra las claves numéricas
            .map((key) => ({
              value: eTypeActionExternal[key as keyof typeof eTypeActionExternal],
              label: key
            }))

          return (
            <TextField
              variant={'outlined'}
              id='typeActionId'
              name='typeActionId'
              select
              value={inputValue}
              onChange={handleInputChange}
              fullWidth
              size='small'
              sx={{
                height: '44px',
                '& .MuiInputBase-root': { height: '44px' }
              }}
            >
              <MenuItem key={-1} value={-1}>
                {'Seleccionar Tipo de Acción...'}
              </MenuItem>
              {externalStatusOptions.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </TextField>
          )
        }
        if (option.key === 'StatusProcessId') {
          const externalStatusOptions = Object.keys(eStatusProcess)
            .filter((key) => isNaN(Number(key))) // Filtra las claves numéricas
            .map((key) => ({
              value: eStatusProcess[key as keyof typeof eStatusProcess],
              label: key
            }))

          return (
            <TextField
              variant={'outlined'}
              id='StatusProcessId'
              name='StatusProcessId'
              select
              value={inputValue}
              onChange={handleInputChange}
              fullWidth
              size='small'
              sx={{
                height: '44px',
                '& .MuiInputBase-root': { height: '44px' }
              }}
            >
              <MenuItem key={-1} value={-1}>
                {'Seleccionar estado proceso...'}
              </MenuItem>
              {externalStatusOptions.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </TextField>
          )
        }
        if (option.key === 'productOriginId' || option.key === 'brandId') {
          const filteredProductOrigins = option.filterParams?.typeStorageId
            ? productOrigins?.filter((po: ProductOrigin) => {
                const typeStorageId = option.filterParams?.typeStorageId
                if (Array.isArray(typeStorageId)) {
                  return typeStorageId.includes(po.typeStorageId)
                }
                return po.typeStorageId === typeStorageId
              })
            : productOrigins

          return (
            <TextField
              variant={'outlined'}
              id='productOriginId'
              name='productOriginId'
              select
              value={inputValue || -1}
              onChange={handleInputChange}
              fullWidth
              size='small'
              sx={{
                height: '44px',
                '& .MuiInputBase-root': { height: '44px' }
              }}
            >
              <MenuItem key={-1} value={-1}>
                {'Seleccionar Marca'}
              </MenuItem>
              {filteredProductOrigins?.map((option: ProductOrigin) => (
                <MenuItem
                  key={option.productOriginId}
                  value={option.productOriginId}
                >
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          )
        }
        if (option.key === 'typePositionId') {
          return (
            <TypePositionDDL
              onChange={handleInputChange}
              value={inputValue}
              fullWidth
              variant='outlined'
              size='small'
              sx={{
                height: '44px',
                '& .MuiInputBase-root': { height: '44px' }
              }}
            />
          )
        }
        if (option.key === 'typeMovementId') {
          return (
            <TypeMovementDDL
              onChange={handleInputChange}
              value={inputValue}
              variant='outlined'
              size='small'
              fullWidth={true}
              sx={{
                height: '44px',
                '& .MuiInputBase-root': { height: '44px' }
              }}
            />
          )
        }
        if (option.key === 'warehouseId') {
          return (
            <WarehouseDDL
              productOriginId={getProductOriginId()}
              fullWidth
              onChange={handleInputChange}
              value={inputValue}
              type='outlined'
              size='small'
              sx={{
                height: '44px',
                '& .MuiInputBase-root': { height: '44px' }
              }}
            />
          )
        }
        if (option.key === 'typeVasId') {
          return (
            <TypeVasDDL
              fullWidth
              onChange={handleInputChange}
              value={inputValue}
              type='outlined'
              size='small'
              sx={{
                height: '44px',
                '& .MuiInputBase-root': { height: '44px' }
              }}
            />
          )
        }
        if (option.key === 'typeStorageId') {
          return (
            <TypeStorageDLL
              onChange={handleInputChange}
              value={inputValue || 0}
              type='outlined'
              fullWidth
              defaultLabel='Tipo de Almacenamiento'
            />
          )
        }
        if (option.key === 'zoneId') {
          return (
            <Fragment>
              <ZoneDDL
                warehouseId={getWarehouseId()}
                fullWidth
                onChange={handleInputChange}
                value={inputValue}
                variant='outlined'
                size='small'
                sx={{
                  height: '44px',
                  '& .MuiInputBase-root': { height: '44px' }
                }}
              />
            </Fragment>
          )
        }
        if (option.key === 'typeOrderId') {
          return (
            <TextField
              id='typeOrderId'
              name='typeOrderId'
              select
              value={inputValue || '-1'}
              onChange={handleInputChange}
              variant='outlined'
              fullWidth
              size='small'
              sx={{
                height: '44px',
                '& .MuiInputBase-root': { height: '44px' }
              }}
            >
              <MenuItem key={'-1'} value={'-1'}>
                {'Seleccionar Tipo de Pedido...'}
              </MenuItem>
              {typesOrder?.map((typeOrder) => (
                <MenuItem
                  key={typeOrder.typeOrderId}
                  value={typeOrder.typeOrderId}
                >
                  {typeOrder.name} ({typeOrder.description})
                </MenuItem>
              ))}
            </TextField>
          )
        }
        if (option.key === 'workOrderStateId') {
          // Usar DEFAULT_WORKORDER_STATE_ORDER como keyfilter por defecto
          const defaultKeyFilter = DEFAULT_WORKORDER_STATE_ORDER

          // Si viene un keyfilter por parámetro (en option.keyfilter), usarlo
          // De lo contrario, usar el defaultKeyFilter
          const keyFilterToUse = option.keyfilter || defaultKeyFilter

          const filtered = keyFilterToUse
            ? workOrderStates?.filter((state) =>
                keyFilterToUse.includes(state.workOrderStateId.toString())
              )
            : workOrderStates || []

          // Obtener valores ya seleccionados y normalizarlos a número
          const excludedValues = selectedFilters
            .filter((filter) => filter.key === 'workOrderStateId')
            .map((filter) => {
              return typeof filter.value === 'string'
                ? parseInt(filter.value, 10)
                : filter.value
            })

          // Filtrar manualmente los estados que ya están seleccionados
          const availableStates = (filtered || []).filter(
            (state) => !excludedValues.includes(state.workOrderStateId)
          )

          // Ordenar los estados disponibles
          let orderedStates = availableStates

          if (option.customOrder) {
            // Si hay customOrder definido, usar sortWithCustomOrder
            orderedStates = sortWithCustomOrder(
              availableStates,
              'workOrderStateId',
              option.customOrder
            )
          } else if (
            DEFAULT_WORKORDER_STATE_ORDER &&
            DEFAULT_WORKORDER_STATE_ORDER.length > 0
          ) {
            // Si no hay customOrder, usar el orden por defecto del archivo config.ts
            orderedStates = [...availableStates].sort((a, b) => {
              const indexA = DEFAULT_WORKORDER_STATE_ORDER.indexOf(
                a.workOrderStateId.toString()
              )
              const indexB = DEFAULT_WORKORDER_STATE_ORDER.indexOf(
                b.workOrderStateId.toString()
              )

              // Si ambos están en el orden por defecto
              if (indexA !== -1 && indexB !== -1) {
                return indexA - indexB
              }

              // Si solo uno está en el orden por defecto
              if (indexA !== -1) return -1
              if (indexB !== -1) return 1

              // Si ninguno está, mantener orden original
              return 0
            })
          }
          // Si no hay ninguno, orderedStates queda con el orden original

          return (
            <Fragment>
              <GenericDLL
                fullWidth
                initialValues={orderedStates}
                onChange={handleInputChange}
                title='Estado'
                name='state'
                valueKey={'workOrderStateId'}
                value={inputValue}
                variant='outlined'
                size='small'
                sx={{
                  height: '44px',
                  '& .MuiInputBase-root': { height: '44px' }
                }}
              />
            </Fragment>
          )
        }
        if (option.key === 'orderOriginId') {
          return (
            <Fragment>
              <GenericDLL
                fullWidth
                initialValues={orderOrigins || []}
                onChange={handleInputChange}
                title='Canal de Venta'
                name='origin'
                valueKey={'orderOriginId'}
                value={inputValue}
                variant='outlined'
                size='small'
                excludedOptions={
                  selectedFilters.find((el) => el.key === 'orderOriginId')
                    ?.value
                }
                sx={{
                  height: '44px',
                  '& .MuiInputBase-root': { height: '44px' }
                }}
              />
            </Fragment>
          )
        }
        if (option.key === 'flowId') {
          return (
            <Fragment>
              <GenericDLL
                fullWidth
                initialValues={flows || []}
                onChange={handleInputChange}
                title='Origen'
                name='description'
                valueKey={'flowId'}
                value={inputValue}
                variant='outlined'
                size='small'
                excludedOptions={
                  selectedFilters.find((el) => el.key === 'flowId')?.value
                }
                sx={{
                  height: '44px',
                  '& .MuiInputBase-root': { height: '44px' }
                }}
              />
            </Fragment>
          )
        }
        if (option.key === 'typeAlertId') {
          return (
            <Fragment>
              <GenericDLL
                fullWidth
                initialValues={typesAlert || []}
                onChange={handleInputChange}
                title='Tipos de Alerta'
                name='name'
                valueKey={'typeAlertId'}
                value={inputValue}
                variant='outlined'
                size='small'
                excludedOptions={
                  selectedFilters.find((el) => el.key === 'typeAlertId')?.value
                }
                sx={{
                  height: '44px',
                  '& .MuiInputBase-root': { height: '44px' }
                }}
              />
            </Fragment>
          )
        }
        if (option.key === 'typeProcessId') {
          return (
            <Fragment>
              <GenericDLL
                fullWidth
                initialValues={typeProcess || []}
                onChange={handleInputChange}
                title='Tipo Proceso'
                name='description'
                valueKey={'typeProcessId'}
                value={inputValue}
                variant='outlined'
                size='small'
                excludedOptions={
                  selectedFilters.find((el) => el.key === 'typeProcessId')
                    ?.value
                }
                sx={{
                  height: '44px',
                  '& .MuiInputBase-root': { height: '44px' }
                }}
              />
            </Fragment>
          )
        }
        if (option.key === 'supplierId') {
          return (
            <Fragment>
              <GenericDLL
                fullWidth
                initialValues={suppliers || []}
                onChange={handleInputChange}
                title='Proveedor'
                name='supplierName'
                valueKey={'supplierId'}
                value={inputValue}
                variant='outlined'
                excludedOptions={
                  selectedFilters.find((el) => el.key === 'supplierId')
                    ?.value
                }
              />
            </Fragment>
          )
        }
        if (option.key === 'orderStateId') {
          return (
            <TextField
              id='orderStateId'
              name='orderStateId'
              select
              value={inputValue || '-1'}
              onChange={handleInputChange}
              variant='outlined'
              fullWidth
              size='small'
              sx={{
                height: '44px',
                '& .MuiInputBase-root': { height: '44px' }
              }}
            >
              <MenuItem key={'-1'} value={'-1'}>
                {'Seleccionar Estado...'}
              </MenuItem>
              {option.options?.map((stateOption) => (
                <MenuItem key={stateOption.value} value={stateOption.value}>
                  {stateOption.label}
                </MenuItem>
              ))}
            </TextField>
          )
        }
        if (option.key === 'asnStateId') {
          const filteredAsnState = option.keyfilter
            ? asnStates?.filter((state) =>
                option.keyfilter!.includes(state.asnStateId.toString())
              )
            : asnStates || []

          // Obtener valores ya seleccionados y normalizarlos a número
          const excludedValues = selectedFilters
            .filter((filter) => filter.key === 'asnStateId')
            .map((filter) => {
              // Convertir a número para comparación consistente
              return typeof filter.value === 'string'
                ? parseInt(filter.value, 10)
                : filter.value
            })

          // Filtrar manualmente los estados que ya están seleccionados
          const availableStates = (filteredAsnState || []).filter(
            (state) => !excludedValues.includes(state.asnStateId) // Comparar número con número
          )

          // Ordenar los estados disponibles usando customOrder si existe
          const orderedStates = option.customOrder
            ? sortWithCustomOrder(
                availableStates,
                'asnStateId',
                option.customOrder
              )
            : availableStates // Mantén el orden por defecto

          return (
            <Fragment>
              <GenericDLL
                fullWidth
                initialValues={orderedStates} // Pasar solo los estados disponibles
                onChange={handleInputChange}
                title='Estado Asn'
                name='name'
                valueKey={'asnStateId'}
                value={inputValue}
                variant='outlined'
                size='small'
                sx={{
                  height: '44px',
                  '& .MuiInputBase-root': { height: '44px' }
                }}
              />
            </Fragment>
          )
        }
        if (option.key === 'typeAsnIncomeId') {
          const filteredTypeAsnIncomeState = option.keyfilter
            ? typeAsnIncome?.filter((state) =>
                option.keyfilter!.includes(state.typeAsnIncomeId.toString())
              )
            : typeAsnIncome || []

          return (
            <Fragment>
              <GenericDLL
                fullWidth
                initialValues={filteredTypeAsnIncomeState || []}
                onChange={handleInputChange}
                title='Estado Asn'
                name='name'
                valueKey={'typeAsnIncomeId'}
                value={inputValue}
                variant='outlined'
                size='small'
                excludedOptions={
                  selectedFilters.find((el) => el.key === 'typeAsnIncomeId')
                    ?.value
                }
                sx={{
                  height: '44px',
                  '& .MuiInputBase-root': { height: '44px' }
                }}
              />
            </Fragment>
          )
        }
        if (option.key === 'inventoryCheckStateId') {
          const inventoryCheckOptions = Object.keys(eInventoryCheckState)
            .filter((key) => isNaN(Number(key))) // Filtra las claves numéricas
            .map((key) => ({
              value:
                eInventoryCheckState[key as keyof typeof eInventoryCheckState],
              label: key
            }))

          return (
            <TextField
              variant={'outlined'}
              id='inventoryCheckstateId'
              name='inventoryCheckstateId'
              select
              value={inputValue}
              onChange={handleInputChange}
              fullWidth
              size='small'
              sx={{
                height: '44px',
                '& .MuiInputBase-root': { height: '44px' }
              }}
            >
              <MenuItem key={-1} value={-1}>
                {'Seleccionar Estado...'}
              </MenuItem>
              {inventoryCheckOptions.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </TextField>
          )
        }
        if (option.key === 'batchExpiredStatus') {
          const inventoryCheckOptions = Object.keys(eBatchExpiredStatus)
            .filter((key) => isNaN(Number(key))) // Filtra las claves numéricas
            .map((key) => ({
              value:
                eBatchExpiredStatus[key as keyof typeof eBatchExpiredStatus],
              label: key
            }))

          return (
            <TextField
              variant={'outlined'}
              id='batchExpiredStatus'
              name='batchExpiredStatus'
              select
              value={inputValue}
              onChange={handleInputChange}
              fullWidth
              size='small'
              sx={{
                height: '44px',
                '& .MuiInputBase-root': { height: '44px' }
              }}
            >
              <MenuItem key={-1} value={-1}>
                {'Seleccionar...'}
              </MenuItem>
              {inventoryCheckOptions.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </TextField>
          )
        }
        if (option.key === 'batchControl') {
          const batchControlOptions = Object.keys(eBatchControl)
            .filter((key) => isNaN(Number(key))) // Filtra las claves numéricas
            .map((key) => ({
              value: eBatchControl[key as keyof typeof eBatchControl],
              label: key
            }))

          return (
            <TextField
              variant={'outlined'}
              id='batchControl'
              name='batchControl'
              select
              value={inputValue}
              onChange={handleInputChange}
              fullWidth
              size='small'
              sx={{
                height: '44px',
                '& .MuiInputBase-root': { height: '44px' }
              }}
            >
              <MenuItem key={-1} value={-1}>
                {'Seleccionar...'}
              </MenuItem>
              {batchControlOptions.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </TextField>
          )
        }
        if (option.key === 'isGreenSale') {
          const isGreenSaleOptions = Object.keys(eIsGreenSale)
            .filter((key) => isNaN(Number(key))) // Filtra las claves numéricas
            .map((key) => ({
              value: eIsGreenSale[key as keyof typeof eIsGreenSale],
              label: key
            }))

          return (
            <TextField
              variant={'outlined'}
              id='isGreenSale'
              name='isGreenSale'
              select
              value={inputValue}
              onChange={handleInputChange}
              fullWidth
              size='small'
              sx={{
                height: '44px',
                '& .MuiInputBase-root': { height: '44px' }
              }}
            >
              <MenuItem key={-1} value={-1}>
                {'Seleccionar...'}
              </MenuItem>
              {isGreenSaleOptions.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </TextField>
          )
        }
        if (option.key === 'typeInventoryCheckId') {
          const inventoryCheckOptions = Object.keys(eTypeInventoryCheck)
            .filter((key) => isNaN(Number(key))) // Filtra las claves numéricas
            .map((key) => ({
              value:
                eTypeInventoryCheck[key as keyof typeof eTypeInventoryCheck],
              label: key
            }))

          return (
            <TextField
              variant={'outlined'}
              id='typeInventoryCheckId'
              name='typeInventoryCheckId'
              select
              value={inputValue}
              onChange={handleInputChange}
              fullWidth
              size='small'
              sx={{
                height: '44px',
                '& .MuiInputBase-root': { height: '44px' }
              }}
            >
              <MenuItem key={-1} value={-1}>
                {'Seleccionar Tipo...'}
              </MenuItem>
              {inventoryCheckOptions.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </TextField>
          )
        }
        if (option.key === 'courierId') {
          const arrCourierDDLItems =
            couriers !== undefined
              ? couriers.map((option: Courier) => (
                  <MenuItem key={option.courierId} value={option.courierId}>
                    {option.name}
                  </MenuItem>
                ))
              : []

          return (
            <TextField
              id='courierId'
              name='courierId'
              select
              value={inputValue || '-1'}
              onChange={handleInputChange}
              variant='outlined'
              fullWidth
              size='small'
              sx={{
                height: '44px',
                '& .MuiInputBase-root': { height: '44px' }
              }}
            >
              <MenuItem key={'-1'} value={'-1'}>
                {'Seleccionar Courier...'}
              </MenuItem>
              {arrCourierDDLItems}
            </TextField>
          )
        }
        if (option.key === 'courierServiceId') {
          // 1. Creamos una lista plana de TODOS los servicios
          //    y guardamos el nombre del courier padre para claridad.
          const allServicesWithName = couriers
            ? couriers.flatMap((courier) =>
                courier.courierServices.map((service) => ({
                  ...service,
                  courierName: courier.name // Añadimos el nombre del courier
                }))
              )
            : []

          // 2. Verificamos si hay servicios
          if (allServicesWithName.length < 1) {
            return (
              <Typography variant='body2'>
                No hay servicios de courier disponibles.
              </Typography>
            )
          }

          // 3. Creamos los <MenuItem> a partir de la lista plana
          const arrCourierServicesDDLItems = allServicesWithName.map(
            (option) => (
              <MenuItem
                key={`${option.courierName}-${option.courierServiceId}`} // Clave más robusta
                value={option.courierServiceId}
              >
                {/* Mostramos ambos nombres para evitar confusión */}
                {option.courierName} - {option.name}
              </MenuItem>
            )
          )

          // 4. Renderizamos el dropdown
          return (
            <TextField
              id='courierServiceId'
              name='courierServiceId'
              select
              value={inputValue || -1}
              onChange={handleInputChange}
              variant='outlined'
              fullWidth
              size='small'
              sx={{
                height: '44px',
                '& .MuiInputBase-root': { height: '44px' }
              }}
            >
              <MenuItem key={-1} value={-1}>
                {'Seleccionar Servicio de Courier...'}
              </MenuItem>
              {arrCourierServicesDDLItems}
            </TextField>
          )
        }
        if (
          option.key === 'active' ||
          option.key === 'externalStorage' ||
          option.key === 'withVas'
        ) {
          const textValue =
            option.key === 'active' ? 'Seleccionar Estado...' : 'Seleccione ...'

          return (
            <TextField
              variant='outlined'
              id={option.key}
              name={option.key}
              select
              value={inputValue || '-1'}
              onChange={handleInputChange}
              fullWidth
              size='small'
              sx={{
                height: '44px',
                '& .MuiInputBase-root': { height: '44px' }
              }}
            >
              <MenuItem key={-1} value={-1}>
                {textValue}
              </MenuItem>
              <MenuItem key={0} value={'false'}>
                No
              </MenuItem>
              <MenuItem key={1} value={'true'}>
                Sí
              </MenuItem>
            </TextField>
          )
        }
        if (option.key === 'isDraft') {
          return (
            <TextField
              variant='outlined'
              id='isDraft'
              name='isDraft'
              select
              value={inputValue || '-1'}
              onChange={handleInputChange}
              fullWidth
              size='small'
              sx={{
                height: '44px',
                '& .MuiInputBase-root': { height: '44px' }
              }}
            >
              <MenuItem key={-1} value={-1}>
                {'Seleccionar Borrador...'}
              </MenuItem>
              <MenuItem key={0} value={'false'}>
                No
              </MenuItem>
              <MenuItem key={1} value={'true'}>
                Sí
              </MenuItem>
            </TextField>
          )
        }
        if (option.key === 'webhookEventsIds') {
          const appliedValues = selectedFilters
            .filter((f) => f.key === 'webhookEventsIds')
            .map((f) => String(f.value))

          const webHookEventsOptions = Object.keys(eWebhookEvents)
            .filter((key) => isNaN(Number(key)))
            .map((key) => ({
              value: eWebhookEvents[key as keyof typeof eWebhookEvents],
              label: key
            }))
            .filter((opt) => !appliedValues.includes(String(opt.value)))

          return (
            <TextField
              variant={'outlined'}
              id='webhookEventsIds'
              name='webhookEventsIds'
              select
              value={inputValue}
              onChange={handleInputChange}
              fullWidth
              size='small'
              sx={{
                height: '44px',
                '& .MuiInputBase-root': { height: '44px' }
              }}
            >
              <MenuItem key={-1} value={-1}>
                {'Seleccionar...'}
              </MenuItem>
              {webHookEventsOptions.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </TextField>
          )
        }
        if (option.key === 'asnStateId' || option.key === 'detailAsnStateId') {
          const filteredAsnState = option.keyfilter
            ? asnStates?.filter((state) =>
                option.keyfilter!.includes(state.asnStateId.toString())
              )
            : asnStates || []

          // Obtener valores ya seleccionados y normalizarlos a número
          const excludedValues = selectedFilters
            .filter((filter) => filter.key === 'asnStateId')
            .map((filter) => {
              // Convertir a número para comparación consistente
              return typeof filter.value === 'string'
                ? parseInt(filter.value, 10)
                : filter.value
            })

          // Filtrar manualmente los estados que ya están seleccionados
          const availableStates = (filteredAsnState || []).filter(
            (state) => !excludedValues.includes(state.asnStateId) // Comparar número con número
          )

          // Ordenar los estados disponibles usando customOrder si existe
          const orderedStates = option.customOrder
            ? sortWithCustomOrder(
                availableStates,
                'asnStateId',
                option.customOrder
              )
            : availableStates // Mantén el orden por defecto

          return (
            <Fragment>
              <GenericDLL
                fullWidth
                initialValues={orderedStates} // Pasar solo los estados disponibles
                onChange={handleInputChange}
                title='Estado Asn'
                name='name'
                valueKey={'asnStateId'}
                value={inputValue}
                variant='outlined'
                size='small'
                sx={{
                  height: '44px',
                  '& .MuiInputBase-root': { height: '44px' }
                }}
              />
            </Fragment>
          )
        }
        if (option.key === 'typePositionCode' || option.key === 'typePositionDescription') {
          return (
            <TypePositionAutocomplete
              handleChange={handleInputChange}
              positionValue={inputValue}
              typePositionNameList={option.key as any}
              fullWidth
              variant='outlined'
              size='small'
            />
          )
        }
        break
      case 'yesornotoption':
        return (
          <TextField
            variant='outlined'
            id={option.key}
            name={option.key}
            select
            value={inputValue || '-1'}
            onChange={handleInputChange}
            fullWidth
            size='small'
            sx={{ height: '44px', '& .MuiInputBase-root': { height: '44px' } }}
          >
            <MenuItem key='-1' value='-1' disabled>
              Seleccionar...
            </MenuItem>
            <MenuItem value='true'>Sí</MenuItem>
            <MenuItem value='false'>No</MenuItem>
          </TextField>
        )
      case 'custom':
        if (option.Component) {
          return (
            <option.Component
              value={inputValue}
              onChange={handleInputChange}
              fullWidth={true}
              variant='outlined'
              transformForBackend={option.transformForBackend}
              transformForDisplay={option.transformForDisplay}
            />
          )
        }
        break
      case 'daterange':
        return (
          // position relative + error absoluto: el mensaje no ocupa alto en el
          // flujo, así el contenedor sigue midiendo 44px y los inputs no se
          // desplazan hacia arriba al aparecer el error
          <Grid container spacing={1} sx={{ position: 'relative' }}>
            <Grid item xs={6}>
              <TextField
                type='date'
                label='Fecha Inicial'
                value={dateRangeStart}
                onChange={handleDateRangeStartChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
                variant='outlined'
                size='small'
                error={!!dateRangeError}
                sx={{
                  height: '44px',
                  '& .MuiInputBase-root': { height: '44px' }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                type='date'
                label='Fecha Final'
                value={dateRangeEnd}
                onChange={handleDateRangeEndChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
                variant='outlined'
                size='small'
                error={!!dateRangeError}
                sx={{
                  height: '44px',
                  '& .MuiInputBase-root': { height: '44px' }
                }}
              />
            </Grid>
            {dateRangeError && (
              <Tooltip title={dateRangeError} placement='bottom-start'>
                <Typography
                  variant='caption'
                  color='error'
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: '8px',
                    right: 0,
                    mt: '2px',
                    fontSize: '0.65rem',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {dateRangeError}
                </Typography>
              </Tooltip>
            )}
          </Grid>
        )
      default:
        return <Typography>No se ha encontrado el filtro</Typography>
    }

    return null
  }

  const groupedFilters = selectedFilters.reduce(
    (acc, item) => {
      acc[item.key] = acc[item.key] || []
      acc[item.key].push(item)
      return acc
    },
    {} as Record<string, Filter[]>
  )

  const uniqueKeys: Filter[] = []
  const repeatedKeys: {
    key: string
    items: Filter[]
  }[] = []

  const capitalize = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()

  Object.entries(groupedFilters).forEach(([key, items]) => {
    if (items.length > 1) {
      repeatedKeys.push({ key, items })
    } else {
      uniqueKeys.push(...items)
    }
  })

  return (
    <Grid container spacing={2} alignItems='center'>
      <Grid item xs={12} sm={6} md={3}>
        <FormControl fullWidth>
          <Select
            value={selectedFilterKey || ''}
            onChange={handleFilterSelection}
            displayEmpty
            variant='outlined'
            size='small'
            sx={{
              height: '44px',
              minHeight: '44px',
              '& .MuiInputBase-root': { height: '44px' }
            }}
          >
            <MenuItem value=''>
              <em>
                {availableOptions.length > 0
                  ? `Seleccionar...`
                  : 'Ha seleccionado todos los filtros disponibles.'}
              </em>
            </MenuItem>
            {availableOptions
              .sort((a, b) => a.order - b.order)
              .map((option) => (
                <MenuItem key={option.key} value={option.key}>
                  {option.label}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid
        container
        item
        xs={12}
        sm={6}
        md={3}
        alignContent='center'
        alignItems='center'
        justifyContent='center'
        sx={{ height: '44px' }}
      >
        {selectedOption &&
          availableOptions.length > 0 &&
          renderFilterInput(
            selectedOption,
            inputValue,
            handleInputChange,
            selectedFilters
          )}
        {!selectedOption && <Typography>Debe seleccionar un filtro</Typography>}
      </Grid>
      <Grid
        item
        xs={12}
        sm={12}
        md={includeAddButton ? 6 : 5}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'wrap'
        }}
      >
        {includeAddButton && (
          <Tooltip title='Agregar filtro de busqueda'  placement='top'>
            <Button
              onClick={() => addFilter()}
              variant='outlined'
              color='primary'
              disabled={isAddFilterButtonDisabled()}
              startIcon={<FilterAltIcon />}
              sx={{
                whiteSpace: 'nowrap',
                height: '44px',
                borderRadius: '12px',
                flex: '0 0 auto',
                minWidth: '100px',
                px: 0.8,
                '&:hover': {
                  backgroundColor: '#004bb1',
                  color: 'white',
                  borderColor: '#004bb1'
                }
              }}
            >
              Añadir
            </Button>
          </Tooltip>
        )}
        <ButtonGroup
          ref={searchButtonGroupRef}
          aria-label='split button search'
          sx={{
            height: '44px',
            borderRadius: '12px',
            flex: { xs: '0 0 calc(50% - 4px)', sm: '0 0 calc(50% - 4px)', md: '0 0 auto' },
            '& .MuiButtonGroup-grouped:first-of-type': {
              borderTopLeftRadius: '12px',
              borderBottomLeftRadius: '12px'
            },
            '& .MuiButtonGroup-grouped:last-of-type': {
              borderTopRightRadius: '12px',
              borderBottomRightRadius: '12px'
            },
            '& .MuiButtonGroup-grouped:not(:last-of-type)': {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0
            },
            '& .MuiButtonGroup-grouped:not(:first-of-type)': {
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0
            }
          }}
        >
          <Tooltip title='Busqueda por filtros seleccionados' placement='top'>
            <Button
              onClick={handleSearchButtonClick}
              disabled={isButtonDisabled()}
              startIcon={<SearchIcon />}
              sx={{
                textTransform: 'uppercase',
                fontSize: '0.875rem',
                whiteSpace: 'nowrap',
                backgroundColor: '#004bb1',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#003a88'
                },
                '&:disabled': {
                  backgroundColor: 'white',
                  borderColor: '#e0e0e0',
                  color: '#9e9e9e'
                }
              }}
            >
              Buscar
            </Button>
          </Tooltip>

          <Button
            aria-controls={searchMenuOpen ? 'search-menu' : undefined}
            aria-expanded={searchMenuOpen ? 'true' : undefined}
            aria-label='search options'
            aria-haspopup='menu'
            onClick={handleSearchMenuToggle}
            disabled={isButtonDisabled()}
            sx={{
              px: 1,
              minWidth: 'auto',
              backgroundColor: '#004bb1',
              color: 'white',
              '&:hover': {
                backgroundColor: '#003a88'
              },
              '&:disabled': {
                backgroundColor: 'white',
                borderColor: '#e0e0e0',
                color: '#9e9e9e'
              }
            }}
          >
            <ArrowDropDownIcon />
          </Button>
        </ButtonGroup>
        <Popper
          sx={{ zIndex: 1300 }}
          open={searchMenuOpen}
          anchorEl={searchButtonGroupRef.current}
          role={undefined}
          transition
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === 'bottom' ? 'center top' : 'center bottom'
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleSearchMenuClose}>
                  <MenuList id='search-menu' autoFocusItem>
                    <Tooltip title='Limpiar filtros de busqueda' placement='right'>
                      <MenuItem onClick={handleCleanClick}>
                        <CleaningServicesIcon
                          sx={{ mr: 1, fontSize: '1.25rem' }}
                        />
                        Limpiar
                      </MenuItem>
                    </Tooltip>

                    {/* <Tooltip title='Refrescar busqueda'>
                      <MenuItem
                        onClick={() => {
                          onSearchClick(selectedFilters)
                          handleSearchMenuClose()
                        }}
                      >
                        <RotateLeftIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
                        Refrescar
                      </MenuItem>
                    </Tooltip> */}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
        {extraActions && extraActions.length > 0 && (
          <>
            <Button
              ref={extraMenuRef}
              variant='outlined'
              color='primary'
              onClick={handleExtraMenuToggle}
              startIcon={<QueueIcon />}
              endIcon={<ArrowDropDownIcon />}
              sx={{
                whiteSpace: 'nowrap',
                height: '44px',
                borderRadius: '12px',
                flex: '0 0 auto',
                minWidth: '130px',
                px: 0.8,
                '& .MuiButton-endIcon': { ml: 0.1 },
                '&:hover': {
                  backgroundColor: '#004bb1',
                  color: 'white',
                  borderColor: '#004bb1'
                }
              }}
            >
              Más acciones
            </Button>

            <Popper
              sx={{ zIndex: 1300 }}
              open={extraMenuOpen}
              anchorEl={extraMenuRef.current}
              role={undefined}
              transition
              disablePortal
            >
              {({ TransitionProps, placement }) => (
                <Grow
                  {...TransitionProps}
                  style={{
                    transformOrigin:
                      placement === 'bottom' ? 'center top' : 'center bottom'
                  }}
                >
                  <Paper>
                    <ClickAwayListener onClickAway={handleExtraMenuClose}>
                      <MenuList autoFocusItem>
                        {extraActions.map((action, index) =>
                          action.tooltip ? (
                            <Tooltip
                              key={index}
                              title={action.tooltip}
                              placement='top'
                              arrow
                            >
                              <span>
                                <MenuItem
                                  disabled={action.disabled || false}
                                  onClick={() => {
                                    action.onClick()
                                    handleExtraMenuClose()
                                  }}
                                >
                                  {action.icon && (
                                    <Box sx={{ mr: 1, display: 'flex' }}>
                                      {action.icon}
                                    </Box>
                                  )}
                                  {action.label}
                                </MenuItem>
                              </span>
                            </Tooltip>
                          ) : (
                            <MenuItem
                              key={index}
                              disabled={action.disabled || false}
                              onClick={() => {
                                action.onClick()
                                handleExtraMenuClose()
                              }}
                            >
                              {action.icon && (
                                <Box sx={{ mr: 1, display: 'flex' }}>
                                  {action.icon}
                                </Box>
                              )}
                              {action.label}
                            </MenuItem>
                          )
                        )}
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </>
        )}

        {onAddButtonClick && (
          <Tooltip title={createButtonTooltip || 'Crear'} placement='top'>
            <Button
              variant='outlined'
              color='primary'
              onClick={onAddButtonClick}
              startIcon={<AddIcon />}
              sx={{
                whiteSpace: 'nowrap',
                height: '44px',
                borderRadius: '12px',
                flex: '0 0 auto',
                minWidth: '100px',
                px: 0.8,
                '&:hover': {
                  backgroundColor: '#004bb1',
                  color: 'white',
                  borderColor: '#004bb1'
                }
              }}
            >
              {addButtonTitle || 'Crear'}
            </Button>
          </Tooltip>
        )}
        {rightSlot && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {rightSlot}
          </Box>
        )}
      </Grid>
      <Grid container item xs={12}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '10px'
          }}
        >
          {uniqueKeys.map((item) => (
            <Chip
              key={item.key}
              label={
                item.label === ''
                  ? item.displayValue
                  : `${item.label}: ${capitalize(item.displayValue)}`
              }
              // style={{ backgroundColor: 'lightblue' }}
              onClick={() => handleEditFilter(item)}
              onDelete={() => handleDeleteFilter(item)}
            />
          ))}
        </div>
      </Grid>

      <Grid container item xs={12}>
        {repeatedKeys.map(({ key, items }) => (
          <Fragment>
            {items.length > 1 ? (
              <div
                key={key}
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '10px',
                  padding: '10px',
                  border: '1px dashed #ccc',
                  borderRadius: '10px'
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: '-1px', // Ajustar según el borde
                    left: '10px',
                    backgroundColor: '#fff',
                    padding: '0 5px',
                    color: '#333',
                    fontWeight: 'bold',
                    transform: 'translateY(-50%)'
                  }}
                >
                  <Typography style={{ marginRight: '10px' }}>
                    {items[0].label}
                  </Typography>
                </div>

                {items.map((item) => (
                  <Chip
                    key={item.value}
                    label={capitalize(item.displayValue)}
                    onClick={() => handleEditFilter(item)}
                    onDelete={() => handleDeleteFilter(item)}
                  />
                ))}
              </div>
            ) : (
              <Chip
                key={key}
                label={setFormatTextChip(items[0])}
                onClick={() => handleEditFilter(items[0])}
                onDelete={() => handleDeleteFilter(items[0])}
              />
            )}
          </Fragment>
        ))}
      </Grid>
    </Grid>
  )
}
